import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { showsBottomBanners } from '../utils/adVisibility';

import { hasPremiumAccess } from '../config/subscriptions';
/**
 * AdSlot Component
 * Displays bundreklamer (bottom banners) og lignende ad-formater.
 * Visibility styres af `showsBottomBanners(user)` — pt. gæster + gratis-tier.
 * Pro/Editor/Admin/Light ser aldrig disse bannere (Light ser i stedet
 * fuld-bredde sponsor-rækker inde i opskriftsgriddet).
 * Roterer automatisk hver 30 sek og ved navigation.
 */
const AdSlot = ({ placement = 'bottom_banner' }) => {
  const { user } = useAuth();
  const location = useLocation(); // Detect navigation changes
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availableAds, setAvailableAds] = useState([]);
  const [bannerSlots, setBannerSlots] = useState([]); // fixed 9-slot bottom banner
  const [animating, setAnimating] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [carouselStartIndex, setCarouselStartIndex] = useState(0);

  useEffect(() => {
    // Skjul bundreklamer for tiers der ikke skal se dem.
    if (!showsBottomBanners(user)) {
      setLoading(false);
      return;
    }

    fetchAds();
  }, [user, placement]);

  // Carousel rotation for bottom banners (always 9 slots → cycle 3 at a time)
  useEffect(() => {
    if (!showsBottomBanners(user) || placement !== 'bottom_banner') {
      return;
    }
    if (bannerSlots.length <= 3) return;

    const rotationInterval = setInterval(() => {
      setCarouselStartIndex((prev) => {
        const next = prev + 3;
        return next >= bannerSlots.length ? 0 : next;
      });
    }, 15000);

    return () => clearInterval(rotationInterval);
  }, [user, bannerSlots, placement]);

  // Separate effect for rotation - runs when availableAds change (for regular ads)
  useEffect(() => {
    if (!showsBottomBanners(user) || availableAds.length <= 1 || placement === 'bottom_banner') {
      return;
    }

    // Rotate ads every 15 seconds
    const rotationInterval = setInterval(() => {
      setAnimating(true); // Start zoom animation
      
      setTimeout(() => {
        setAd(currentAd => {
          if (availableAds.length > 1) {
            // Pick a different ad than current
            const otherAds = availableAds.filter(a => a.id !== currentAd?.id);
            if (otherAds.length > 0) {
              const newAd = otherAds[Math.floor(Math.random() * otherAds.length)];
              console.log('Rotating ad:', newAd.title || newAd.id);
              return newAd;
            }
          }
          return currentAd;
        });
        
        // Reset animation after ad changes
        setTimeout(() => setAnimating(false), 50);
      }, 300); // Brief delay before changing ad
      
    }, 15000); // 15 seconds

    return () => clearInterval(rotationInterval);
  }, [user, availableAds]);

  // Rotate ad on navigation/location change
  useEffect(() => {
    if ((!hasPremiumAccess(user)) && availableAds.length > 1) {
      rotateAd();
    }
  }, [location.pathname]);

  const fetchAds = async () => {
    try {
      // Detect country from browser (fallback to 'DK')
      const country = getCountryCode();

      // Bottom banner uses the fixed 9-slot system – the area must NEVER be
      // empty. Empty/missing slots come back as placeholder objects.
      if (placement === 'bottom_banner') {
        const response = await axios.get(`${API}/banner-slots`, {
          params: { country },
        });
        const slots = response.data?.slots || [];
        setBannerSlots(slots);
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/ads`, {
        params: {
          country,
          placement
        }
      });

      if (response.data && response.data.length > 0) {
        console.log(`Fetched ${response.data.length} ads for placement: ${placement}`);
        
        // Preload images to catch errors early
        const adsWithValidImages = await Promise.all(
          response.data.map(async (ad) => {
            return new Promise((resolve) => {
              const img = new Image();
              img.onload = () => {
                console.log('✓ Ad image loaded:', ad.title, ad.image);
                resolve(ad);
              };
              img.onerror = () => {
                console.error('✗ Ad image failed:', ad.title, ad.image);
                // Still include ad, but mark for fallback
                ad._imageError = true;
                resolve(ad);
              };
              img.src = ad.image;
              
              // Timeout after 5 seconds
              setTimeout(() => {
                if (!img.complete) {
                  console.warn('⏱ Ad image timeout:', ad.title, ad.image);
                  ad._imageError = true;
                  resolve(ad);
                }
              }, 5000);
            });
          })
        );
        
        setAvailableAds(adsWithValidImages);
        
        // Set initial ad (prefer one without image errors)
        const goodAd = adsWithValidImages.find(a => !a._imageError) || adsWithValidImages[0];
        setAd(goodAd);
      } else {
        console.log('No ads available for placement:', placement);
      }
    } catch (error) {
      console.error('Error fetching ad:', error);
    } finally {
      setLoading(false);
    }
  };

  const rotateAd = () => {
    if (availableAds.length > 1) {
      // Pick a different ad than current
      const otherAds = availableAds.filter(a => a.id !== ad?.id);
      if (otherAds.length > 0) {
        const newAd = otherAds[Math.floor(Math.random() * otherAds.length)];
        setAd(newAd);
      }
    }
  };

  const getCountryCode = () => {
    // Try to get country from browser language
    const language = navigator.language || navigator.userLanguage;
    
    // Map common language codes to countries
    const countryMap = {
      'da': 'DK',
      'da-DK': 'DK',
      'de': 'DE',
      'de-DE': 'DE',
      'en-GB': 'GB',
      'en-US': 'US',
      'fr': 'FR',
      'fr-FR': 'FR',
    };

    return countryMap[language] || 'DK'; // Default to Denmark
  };

  const handleClick = async (adItem = ad) => {
    if (adItem) {
      // Track click
      try {
        await axios.post(`${API}/ads/${adItem.id}/click`);
      } catch (error) {
        console.error('Error tracking click:', error);
      }
    }
  };

  // Inactive banners are still rendered as a "denne plads kan blive din"
  // placeholder – clicks route to the internal /advertise page instead of
  // the advertiser's URL. Active banners keep their normal external link.
  const ADVERTISE_PATH = '/advertise';
  const getBannerTarget = (adItem) => {
    if (!adItem) return { href: ADVERTISE_PATH, external: false };
    const isActive = adItem.active !== false; // default to active if flag missing
    if (isActive && adItem.link) {
      return { href: adItem.link, external: true };
    }
    return { href: ADVERTISE_PATH, external: false };
  };

  // Hide entirely for anyone who shouldn't see bottom banners (Light/Pro/
  // Editor/Admin). Bottom_banner is the primary placement so this covers
  // the crash we saw on Light tier where role='guest' but subscription!='free'.
  if (!showsBottomBanners(user)) return null;
  if (loading) return null;
  if (placement !== 'bottom_banner' && !ad) return null;

  // Different styles based on placement
  const placementStyles = {
    bottom_banner: 'fixed left-0 right-0 z-50 shadow-lg bottom-16 md:bottom-0',
    recipe_list: 'w-full my-4',
    homepage_hero: 'w-full max-w-6xl mx-auto my-6',
    sidebar: 'w-full'
  };

  const isBottomBanner = placement === 'bottom_banner';

  // Bottom banner: ALWAYS render 9 fixed slots. Placeholders fill empty ones
  // and route to /advertise. Real ads route externally.
  if (isBottomBanner && bannerSlots.length > 0) {
    const currentAds = [];
    for (let i = 0; i < 3; i++) {
      const index = (carouselStartIndex + i) % bannerSlots.length;
      currentAds.push(bannerSlots[index]);
    }

    return (
      <div
        className={`${placementStyles[placement]}`}
        style={{
          backgroundImage: 'url(/ad-background.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 py-2">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentAds.map((slot, index) => {
              const isPlaceholder = slot.is_placeholder === true;
              const linkProps = isPlaceholder
                ? { href: '/advertise' }
                : { href: slot.link, target: '_blank', rel: 'noopener noreferrer' };
              return (
                <a
                  key={`slot-${slot.slot_number}-${carouselStartIndex}`}
                  {...linkProps}
                  onClick={() => !isPlaceholder && handleClick(slot)}
                  data-testid={isPlaceholder ? `ad-slot-placeholder-${slot.slot_number}` : `ad-slot-${slot.slot_number}`}
                  data-slot={slot.slot_number}
                  data-placeholder={isPlaceholder}
                  className={`block cursor-pointer overflow-hidden rounded-xl shadow-md backdrop-blur-md border transition-all active:scale-[0.98] ${
                    isPlaceholder && !slot.image
                      ? 'bg-gradient-to-br from-cyan-500/90 to-blue-600/90 border-white/40 hover:from-cyan-500 hover:to-blue-600'
                      : 'bg-white/85 border-gray-200/50 hover:shadow-lg'
                  } ${index === 2 ? 'hidden lg:block' : index === 1 ? 'hidden md:block' : ''}`}
                >
                  <div className="p-3">
                    {slot.image && !slot._imageError ? (
                      <img
                        src={slot.image}
                        alt={slot.title || (isPlaceholder ? 'Bliv vores annoncør' : 'Reklame')}
                        className="w-full h-auto object-contain h-32 md:h-16 lg:h-16"
                        style={{ display: 'block' }}
                        onError={(e) => {
                          console.error('Ad image failed to load:', slot.image);
                          e.target.style.display = 'none';
                        }}
                        loading="eager"
                      />
                    ) : isPlaceholder ? (
                      <div className="w-full h-32 md:h-16 lg:h-16 flex flex-col items-center justify-center text-white text-center">
                        <div className="text-sm font-bold leading-tight">Denne plads kan blive din</div>
                        <div className="text-xs opacity-90 mt-0.5">Klik for at annoncere</div>
                      </div>
                    ) : (
                      <div className="w-full h-32 md:h-16 lg:h-16 bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center text-white font-semibold text-sm rounded-lg">
                        {slot.title || 'Reklame'}
                      </div>
                    )}
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Regular banner (not bottom) — kun rendere hvis vi faktisk har en ad.
  // Uden dette null-guard falder bottom_banner-slots med tom bannerSlots-pulje
  // igennem hertil og forsøger at læse `ad.id` på null → runtime crash.
  if (!ad) return null;
  const { href: regularHref, external: regularExternal } = getBannerTarget(ad);
  const regularLinkProps = regularExternal
    ? { href: regularHref, target: '_blank', rel: 'noopener noreferrer' }
    : { href: regularHref };
  return (
    <div 
      className={`${placementStyles[placement]} relative group`}
    >
      {/* Ad Content Container */}
      <div className="relative">
        {/* Ad Content - using <a> tag for better mobile support */}
        <a
          {...regularLinkProps}
          onClick={handleClick}
          data-testid={`ad-banner-${ad.id}`}
          data-active={ad.active !== false}
          className="block cursor-pointer overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-shadow bg-white border border-gray-200 active:scale-[0.98]"
        >
          {/* Image - Full width with better error handling */}
          {!ad._imageError ? (
            <img
              src={ad.image}
              alt={ad.title || 'Reklame'}
              className="w-full h-auto object-cover"
              style={{ display: 'block' }}
              onError={(e) => {
                console.error('Ad image failed to load during display:', ad.image);
                e.target.style.display = 'none';
              }}
              loading="eager"
            />
          ) : (
            // Fallback for failed images
            <div className="w-full h-48 bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center text-white font-semibold">
              {ad.title || 'Reklame'}
            </div>
          )}

          {/* Optional Title/Description */}
          {(ad.title || ad.description) && (
            <div className="p-4 bg-white/90">
              {ad.title && (
                <h3 className="font-semibold text-gray-800 mb-1">{ad.title}</h3>
              )}
              {ad.description && (
                <p className="text-sm text-gray-600">{ad.description}</p>
              )}
            </div>
          )}
        </a>
      </div>
    </div>
  );
};

export default AdSlot;
